import { Fragment, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'core/contexts/localization';
import { useAuth } from 'core/contexts/auth';
import { useLayout } from 'core/contexts/layout';
import { StyledTooltip } from './styles';
import WhiteLogo from 'assets/logo/white.png';
import ClosedMenuLogo from 'assets/logo/closed-menu.png';
import { Close, ArrowForwardIos, ArrowBackIos, Menu, ExitToApp } from '@mui/icons-material';
import { Drawer, Link, List, ListItem, ListItemIcon, ListItemText, Typography, IconButton, useMediaQuery, ListItemButton, Box, Grid2, ListSubheader, Divider } from '@mui/material';
import TermsAndConditions from 'components/Menu/TermsAndConditions';

const Sidebar = ({ sections }) => {
    const { logout } = useAuth();
    const { open, setOpen, toggleMenu } = useLayout();
    const { t: translate } = useTranslation();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const history = useHistory();
    const location = useLocation();
    const [openTerms, setOpenTerms] = useState(false);
    const drawerWidth = isMobile ? '100vw' : 240;

    const drawerStyles = {
        width: drawerWidth,
        flexShrink: 0,
    };

    const drawerOpenStyles = {
        width: drawerWidth,
        transition: (theme) =>
            theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
    };

    const drawerCloseStyles = {
        transition: (theme) =>
            theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
        overflowX: 'hidden',
        width: 64,
    };

    const onLogoutClick = async () => {
        await logout();
        history.push('/');
        setOpen(false);
    };

    const renderIcon = ({ src, size = 24, color = '#FFFFFF', display = 'inline-block' }) => (
        <Box
            component="span"
            sx={{
                width: size,
                height: size,
                backgroundColor: color,
                mask: `url(${src}) no-repeat center`,
                WebkitMask: `url(${src}) no-repeat center`,
                maskSize: 'contain',
                WebkitMaskSize: 'contain',
                display,
            }}
        />
    );

    const getActualPath = (path) => {
        const locationPathname = location.pathname?.split('/')[1];
        return path?.split('/')[1] === locationPathname;
    };

    return (
        <Fragment>
            {isMobile && !open && (
                <IconButton
                    edge="start"
                    size="large"
                    onClick={toggleMenu}
                    sx={{
                        position: 'fixed',
                        zIndex: 1200,
                        boxShadow: 3,
                        height: '40px',
                        width: '40px', //
                        backgroundColor: '#131313',
                        borderRadius: '0 4px 4px 0',
                        top: '24px',
                        left: 0,
                        marginLeft: 0,
                        '&:hover': { backgroundColor: '#333' },
                    }}
                >
                    <Menu sx={{ color: '#FFFFFF' }} />
                </IconButton>
            )}
            <Box sx={{ position: 'relative', display: 'flex' }}>
                {!open && !isMobile && (
                    <StyledTooltip arrow title={translate('layout.menu.expand')} placement="right" disableHoverListener={open}>
                        <IconButton
                            onClick={toggleMenu}
                            sx={{
                                position: 'fixed',
                                left: 62,
                                top: '2em',
                                zIndex: 1201,
                                backgroundColor: '#131313',
                                borderRadius: '0 4px 4px 0',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                    backgroundColor: '#333',
                                },
                            }}
                        >
                            <ArrowForwardIos sx={{ fontSize: '16px', color: '#FFFFFF' }} />
                        </IconButton>
                    </StyledTooltip>
                )}
            </Box>
            <Drawer
                open={open}
                variant={isMobile ? 'persistent' : 'permanent'}
                ModalProps={{ keepMounted: true }}
                anchor="left"
                sx={drawerStyles}
                slotProps={{
                    paper: {
                        sx: {
                            width: open ? drawerOpenStyles : drawerCloseStyles,
                            border: 'transparent',
                            backgroundColor: '#131313',
                            height: '100dvh',
                            maxHeight: '-webkit-fill-available',
                            zIndex: 1200,
                        },
                    },
                }}
            >
                {/* Header */}
                <Grid2 container justifyContent={{ xs: 'center', md: 'space-between' }} alignItems="center" padding={(theme) => theme.spacing(4, 2)} pb={2} sx={{ position: 'relative' }}>
                    <Box component="img" src={ClosedMenuLogo} alt="utriper-logo" sx={{ width: 32, height: 32, objectFit: 'contain', display: open ? 'none' : 'flex' }} />
                    <Box
                        component="img"
                        src={WhiteLogo}
                        alt="utriper-logo"
                        sx={{
                            width: 'auto',
                            height: 32,
                            objectFit: 'contain',
                            marginRight: { xs: 'auto', md: 0 },
                            order: { xs: 2, md: 1 },
                            display: open ? 'flex' : 'none',
                        }}
                    />

                    {open && (
                        <IconButton
                            onClick={toggleMenu}
                            sx={{
                                width: 32,
                                height: 32,
                                order: { xs: 1, md: 2 },
                                marginRight: { xs: 'auto', md: 0 },
                            }}
                        >
                            {isMobile ? <Close sx={{ fontSize: '16px', color: '#FFFFFF' }} /> : <ArrowBackIos sx={{ fontSize: '16px', color: '#FFFFFF' }} />}
                        </IconButton>
                    )}
                </Grid2>

                {/* Sections */}
                <Box
                    sx={{
                        flexGrow: 1,
                        overflowY: 'auto',
                        position: 'relative',
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#bbb',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'transparent',
                        },
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#bbb transparent',
                    }}
                >
                    {sections?.map((section) => {
                        const sectionTitle = translate(section?.title);

                        return (
                            <List
                                key={`section-${sectionTitle}`}
                                sx={{ width: '100%', px: open ? '16px' : '8px', boxSizing: 'border-box' }}
                                component="nav"
                                aria-labelledby={`menu-list-${sectionTitle}`}
                                subheader={
                                    <ListSubheader
                                        id={`menu-list-${sectionTitle}`}
                                        component="div"
                                        disableSticky
                                        sx={{
                                            backgroundColor: '#131313',
                                            padding: 0,
                                            maxHeight: '2em',
                                            color: '#7F7F7F',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            justifyContent: open ? 'flex-start' : 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {sectionTitle}
                                    </ListSubheader>
                                }
                            >
                                {section?.items?.map((item) => {
                                    const itemLabel = translate(item?.label);
                                    return (
                                        <ListItem
                                            key={`item-${itemLabel}`}
                                            disablePadding
                                            sx={{
                                                display: 'flex',
                                                justifyContent: open ? 'flex-start' : 'center',
                                                alignItems: 'flex-start',
                                                width: '100%',
                                                paddingY: '8px !important',
                                            }}
                                        >
                                            <StyledTooltip arrow title={itemLabel} placement="right" disableHoverListener={open}>
                                                <ListItemButton
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        color: '#FFFFFF',
                                                        borderRadius: '4px',
                                                        '&.Mui-selected': {
                                                            backgroundColor: '#137dc540',
                                                            color: '#137DC5',
                                                            '&:hover': {
                                                                backgroundColor: '#137dc560',
                                                            },
                                                        },
                                                        '&:hover': {
                                                            //
                                                            backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                                        },
                                                    }}
                                                    onClick={() => {
                                                        history.push(item?.path);
                                                        setOpen(false);
                                                    }}
                                                    selected={getActualPath(item?.path)}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 'auto' }}>{renderIcon({ src: item?.icon, color: getActualPath(item?.path) ? '#137DC5' : '#FFFFFF' })}</ListItemIcon>
                                                    {open && (
                                                        <ListItemText sx={{ marginLeft: '1em' }}>
                                                            <Typography sx={{ fontWeight: 'bold', color: 'inherit' }}>{itemLabel}</Typography>
                                                        </ListItemText>
                                                    )}
                                                </ListItemButton>
                                            </StyledTooltip>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        );
                    })}
                </Box>

                <Divider sx={{ margin: (theme) => theme.spacing(1, 2), borderColor: '#7F7F7F' }} />

                <ListItem
                    sx={{
                        display: 'flex',
                        justifyContent: open ? 'flex-start' : 'center',
                        alignItems: 'flex-start',
                        marginTop: 'auto',
                        marginBottom: (theme) => theme.spacing(1),
                        px: open ? '16px' : '8px',
                        width: '100%',
                    }}
                    disablePadding
                    onClick={onLogoutClick}
                >
                    <StyledTooltip arrow title={translate('layout.menu.sign_out')} placement="right" disableHoverListener={open}>
                        <ListItemButton sx={{ display: 'flex', justifyContent: 'center' }}>
                            <ListItemIcon sx={{ minWidth: 'auto' }}>
                                <ExitToApp sx={{ width: 24, height: 24, color: '#FFFFFF' }} />
                            </ListItemIcon>
                            {open && (
                                <ListItemText sx={{ marginLeft: '1em' }}>
                                    <Typography sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>{translate('layout.menu.sign_out')}</Typography>
                                </ListItemText>
                            )}
                        </ListItemButton>
                    </StyledTooltip>
                </ListItem>

                {open && (
                    <Typography sx={{ padding: (theme) => theme.spacing(2), color: '#FFFFFF' }}>
                        © Global Triper -
                        <Link
                            href=""
                            style={{ color: '#1890ff' }}
                            onClick={(e) => {
                                setOpenTerms(true);
                                e.preventDefault();
                            }}
                        >{` ${translate('termsAndConditions.privacyPolicy')}`}</Link>
                    </Typography>
                )}
            </Drawer>

            <TermsAndConditions open={openTerms} onClose={() => setOpenTerms(false)} />
        </Fragment>
    );
};

Sidebar.displayName = 'Sidebar';

export default Sidebar;
